import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ROLE_LABEL, fmtDateTime } from "@/lib/constants";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sparkles, LayoutDashboard, Database, Radar, Scale, RefreshCcw, TrendingUp,
  ListChecks, Boxes, ScrollText, Settings, Bell, LogOut, Menu, BookOpen,
} from "lucide-react";
import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { cn } from "@/lib/utils";

const NAV = [
  { group: "Conocimiento", items: [
    { to: "/", label: "Knowledge Hub", icon: BookOpen, end: true },
  ]},
  { group: "Gobierno", items: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/registro", label: "AI Registry", icon: Database },
    { to: "/comite", label: "Comité de IA", icon: Scale },
    { to: "/shadow-ai", label: "Shadow AI", icon: Radar },
    { to: "/reevaluaciones", label: "Reevaluaciones", icon: RefreshCcw },
  ]},
  { group: "Valor", items: [
    { to: "/valor", label: "Monitoreo de valor", icon: TrendingUp },
    { to: "/tareas", label: "Mis tareas", icon: ListChecks },
  ]},
  { group: "Catálogos", items: [
    { to: "/registros", label: "Registros especializados", icon: Boxes },
  ]},
  { group: "Administración", items: [
    { to: "/auditoria", label: "Centro de auditoría", icon: ScrollText },
    { to: "/admin", label: "Administración", icon: Settings, adminOnly: true },
  ]},
];

const SidebarContent = ({ onNavigate }) => {
  const { can } = useAuth();
  return (
    <div className="flex flex-col h-full bg-sidebar text-sidebar-foreground">
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 pt-6 pb-8">
        <div className="h-10 w-10 rounded-xl bg-accent/15 border border-accent/25 flex items-center justify-center">
          <Sparkles className="h-5 w-5 text-accent" />
        </div>
        <div>
          <p className="font-semibold text-sm text-foreground leading-tight tracking-tight">VIA</p>
          <p className="text-[10px] tracking-widest uppercase text-sidebar-foreground/60">Valor de la IA</p>
        </div>
      </div>
      {/* Nav */}
      <ScrollArea className="flex-1 px-3">
        <nav className="space-y-6 pb-6">
          {NAV.map((g) => (
            <div key={g.group}>
              <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-sidebar-foreground/40">
                {g.group}
              </p>
              <div className="space-y-0.5">
                {g.items.filter((i) => !i.adminOnly || can.admin).map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-200",
                        isActive
                          ? "bg-accent/10 text-accent border-l-2 border-accent"
                          : "hover:bg-sidebar-hover text-sidebar-foreground hover:text-foreground"
                      )
                    }
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </ScrollArea>
      {/* Footer */}
      <div className="px-5 py-4 border-t border-border/30 text-[10px] text-sidebar-foreground/40">
        UC CHRISTUS · Plataforma de IA
      </div>
    </div>
  );
};

export const AppLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    api.get("/notifications").then(({ data }) => setNotifs(data)).catch(() => {});
  }, []);

  const unread = notifs.filter((n) => !n.read).length;

  const markAll = async () => {
    await api.put("/notifications/read-all");
    setNotifs((n) => n.map((x) => ({ ...x, read: true })));
  };

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-60 z-40 border-r border-border/30">
        <SidebarContent />
      </aside>

      <div className="lg:pl-60 flex flex-col min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-30 h-14 border-b border-border/30 bg-background/90 backdrop-blur-md flex items-center gap-3 px-4 sm:px-6">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-60 border-0">
              <SidebarContent onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>

          <div className="ml-auto flex items-center gap-2">
            {/* Theme toggle */}
            <ThemeToggle />
            {/* Notifications */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative">
                  <Bell className="h-4 w-4" />
                  {unread > 0 && (
                    <span className="absolute top-1 right-1 h-4 min-w-4 px-0.5 rounded-full bg-accent text-[10px] font-bold text-accent-foreground flex items-center justify-center">
                      {unread}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
                  <p className="font-semibold text-sm">Notificaciones</p>
                  {unread > 0 && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-accent" onClick={markAll}>
                      Marcar leídas
                    </Button>
                  )}
                </div>
                <ScrollArea className="max-h-72">
                  {notifs.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">Sin notificaciones</p>}
                  {notifs.slice(0, 10).map((n) => (
                    <div key={n.id} className={cn("px-4 py-3 border-b border-border/30 last:border-0", !n.read && "bg-accent/5")}>
                      <p className="text-xs font-medium">{n.title}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{n.body}</p>
                      <p className="text-[10px] text-muted-foreground/60 mt-1">{fmtDateTime(n.created_at)}</p>
                    </div>
                  ))}
                </ScrollArea>
              </PopoverContent>
            </Popover>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-muted/50 transition-colors">
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="bg-accent/15 text-accent text-[10px] font-semibold">
                      {user?.initials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden md:block text-xs font-medium text-foreground">{user?.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <p className="font-semibold text-sm">{user?.name}</p>
                  <p className="text-xs text-muted-foreground font-normal">{user?.title}</p>
                  <Badge variant="outline" className="mt-2 text-[10px] bg-accent/10 text-accent border-accent/25">
                    {ROLE_LABEL[user?.role]}
                  </Badge>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }}>
                  <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 px-4 sm:px-6 py-6 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
